import { useState } from 'react';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  MapPin,
  Search,
} from 'lucide-react';
import api, { GoogleAdsAccount } from '../services/api';

interface CampaignBlueprint {
  campaign_name: string;
  daily_budget: number;
  bidding_strategy: string;
  target_cpa: number | null;
  target_roas: number | null;
  ad_group_name: string;
  cpc_bid: number | null;
  keywords: Array<{ text: string; match_type: string }>;
  headlines: string[];
  descriptions: string[];
  final_url: string;
}

interface CreateCampaignWizardProps {
  account: GoogleAdsAccount;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: CampaignBlueprint;
}

interface GeoTarget {
  id: number;
  name: string;
  canonical_name: string;
  target_type: string;
}

interface FormData {
  campaign_name: string;
  daily_budget: string;
  bidding_strategy: string;
  cpc_bid: string;
  target_cpa: string;
  target_roas: string;
  ad_group_name: string;
  keywords: Array<{ text: string; match_type: string }>;
  headlines: string[];
  descriptions: string[];
  final_url: string;
  path1: string;
  path2: string;
  locations: GeoTarget[];
}

const INITIAL_FORM: FormData = {
  campaign_name: '',
  daily_budget: '',
  bidding_strategy: 'maximize_clicks',
  cpc_bid: '',
  target_cpa: '',
  target_roas: '',
  ad_group_name: '',
  keywords: [{ text: '', match_type: 'broad' }],
  headlines: ['', '', ''],
  descriptions: ['', ''],
  final_url: '',
  path1: '',
  path2: '',
  locations: [],
};

const BIDDING_OPTIONS = [
  { value: 'maximize_clicks', label: 'Maximize Clicks', description: 'Get the most clicks within your budget' },
  { value: 'maximize_conversions', label: 'Maximize Conversions', description: 'Get the most conversions within your budget' },
  { value: 'manual_cpc', label: 'Manual CPC', description: 'Set your own max cost-per-click bids' },
  { value: 'target_cpa', label: 'Target CPA', description: 'Get conversions at a target cost-per-action' },
  { value: 'target_roas', label: 'Target ROAS', description: 'Get conversion value at a target return on ad spend' },
];

const MATCH_TYPES = [
  { value: 'broad', label: 'Broad' },
  { value: 'phrase', label: 'Phrase' },
  { value: 'exact', label: 'Exact' },
];

const CreateCampaignWizard = ({ account, onClose, onSuccess, initialData }: CreateCampaignWizardProps) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(() => {
    if (!initialData) return INITIAL_FORM;
    return {
      campaign_name: initialData.campaign_name || '',
      daily_budget: initialData.daily_budget ? String(initialData.daily_budget) : '',
      bidding_strategy: initialData.bidding_strategy || 'maximize_clicks',
      cpc_bid: initialData.cpc_bid ? String(initialData.cpc_bid) : '',
      target_cpa: initialData.target_cpa ? String(initialData.target_cpa) : '',
      target_roas: initialData.target_roas ? String(initialData.target_roas) : '',
      ad_group_name: initialData.ad_group_name || '',
      keywords: initialData.keywords?.length ? initialData.keywords : [{ text: '', match_type: 'broad' }],
      headlines: initialData.headlines?.length >= 3 ? initialData.headlines : ['', '', ''],
      descriptions: initialData.descriptions?.length >= 2 ? initialData.descriptions : ['', ''],
      final_url: initialData.final_url || '',
      path1: '',
      path2: '',
      locations: [],
    };
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [bulkKeywords, setBulkKeywords] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState<GeoTarget[]>([]);
  const [locationSearching, setLocationSearching] = useState(false);

  const updateForm = (field: keyof FormData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Location search
  const searchLocations = async () => {
    if (locationQuery.length < 2) return;
    setLocationSearching(true);
    try {
      const result = await api.searchGoogleAdsLocations(account.id, locationQuery);
      setLocationResults(result.locations.map(l => ({
        id: l.id,
        name: l.name,
        canonical_name: l.canonical_name,
        target_type: l.target_type,
      })));
    } catch (err) {
      console.error('Location search failed:', err);
    } finally {
      setLocationSearching(false);
    }
  };

  const addLocation = (loc: GeoTarget) => {
    if (form.locations.some(l => l.id === loc.id)) return;
    updateForm('locations', [...form.locations, loc]);
    setLocationResults([]);
    setLocationQuery('');
  };

  const removeLocation = (id: number) => {
    updateForm('locations', form.locations.filter(l => l.id !== id));
  };

  // Step validation
  const isStep1Valid = () => {
    if (!form.campaign_name.trim() || !form.daily_budget || parseFloat(form.daily_budget) <= 0) return false;
    if (form.bidding_strategy === 'manual_cpc' && (!form.cpc_bid || parseFloat(form.cpc_bid) <= 0)) return false;
    if (form.bidding_strategy === 'target_cpa' && (!form.target_cpa || parseFloat(form.target_cpa) <= 0)) return false;
    if (form.bidding_strategy === 'target_roas' && (!form.target_roas || parseFloat(form.target_roas) <= 0)) return false;
    return true;
  };

  const isStep2Valid = () => {
    const validKeywords = form.keywords.filter(k => k.text.trim());
    return validKeywords.length >= 1 && (form.ad_group_name.trim() !== '');
  };

  const isStep3Valid = () => {
    const validHeadlines = form.headlines.filter(h => h.trim());
    const validDescriptions = form.descriptions.filter(d => d.trim());
    if (validHeadlines.length < 3) return false;
    if (validDescriptions.length < 2) return false;
    if (!form.final_url.trim()) return false;
    if (validHeadlines.some(h => h.length > 30)) return false;
    if (validDescriptions.some(d => d.length > 90)) return false;
    return true;
  };

  // Keyword helpers
  const addKeyword = () => {
    updateForm('keywords', [...form.keywords, { text: '', match_type: 'broad' }]);
  };

  const removeKeyword = (index: number) => {
    if (form.keywords.length <= 1) return;
    updateForm('keywords', form.keywords.filter((_, i) => i !== index));
  };

  const updateKeyword = (index: number, field: 'text' | 'match_type', value: string) => {
    const updated = [...form.keywords];
    updated[index] = { ...updated[index], [field]: value };
    updateForm('keywords', updated);
  };

  const parseBulkKeywords = () => {
    if (!bulkKeywords.trim()) return;
    const lines = bulkKeywords.split('\n').filter(l => l.trim());
    const parsed = lines.map(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        return { text: trimmed.slice(1, -1), match_type: 'exact' };
      } else if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        return { text: trimmed.slice(1, -1), match_type: 'phrase' };
      }
      return { text: trimmed, match_type: 'broad' };
    });
    const existing = form.keywords.filter(k => k.text.trim());
    updateForm('keywords', [...existing, ...parsed]);
    setBulkKeywords('');
  };

  // Headline/description helpers
  const addHeadline = () => {
    if (form.headlines.length >= 15) return;
    updateForm('headlines', [...form.headlines, '']);
  };

  const removeHeadline = (index: number) => {
    if (form.headlines.length <= 3) return;
    updateForm('headlines', form.headlines.filter((_, i) => i !== index));
  };

  const updateHeadline = (index: number, value: string) => {
    const updated = [...form.headlines];
    updated[index] = value;
    updateForm('headlines', updated);
  };

  const addDescription = () => {
    if (form.descriptions.length >= 4) return;
    updateForm('descriptions', [...form.descriptions, '']);
  };

  const removeDescription = (index: number) => {
    if (form.descriptions.length <= 2) return;
    updateForm('descriptions', form.descriptions.filter((_, i) => i !== index));
  };

  const updateDescription = (index: number, value: string) => {
    const updated = [...form.descriptions];
    updated[index] = value;
    updateForm('descriptions', updated);
  };

  // Submit
  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const validKeywords = form.keywords.filter(k => k.text.trim());
      const validHeadlines = form.headlines.filter(h => h.trim());
      const validDescriptions = form.descriptions.filter(d => d.trim());

      await api.createGoogleAdsFullCampaign(account.id, {
        campaign_name: form.campaign_name.trim(),
        daily_budget: parseFloat(form.daily_budget),
        bidding_strategy: form.bidding_strategy,
        ad_group_name: form.ad_group_name.trim() || `${form.campaign_name.trim()} - Ad Group`,
        keywords: validKeywords,
        headlines: validHeadlines,
        descriptions: validDescriptions,
        final_url: form.final_url.trim(),
        cpc_bid: form.cpc_bid ? parseFloat(form.cpc_bid) : undefined,
        target_cpa: form.target_cpa ? parseFloat(form.target_cpa) : undefined,
        target_roas: form.target_roas ? parseFloat(form.target_roas) : undefined,
        path1: form.path1.trim() || undefined,
        path2: form.path2.trim() || undefined,
        geo_target_ids: form.locations.length > 0 ? form.locations.map(l => l.id) : undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const validKeywordCount = form.keywords.filter(k => k.text.trim()).length;
  const validHeadlineCount = form.headlines.filter(h => h.trim()).length;
  const validDescriptionCount = form.descriptions.filter(d => d.trim()).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Create Search Campaign</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{account.name} ({account.customer_id})</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 px-4 pt-4">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-green-500 text-white' :
                s === step ? 'bg-primary-500 text-black' :
                'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
                {s < step ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 4 && <div className={`flex-1 h-0.5 mx-2 ${s < step ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} />}
            </div>
          ))}
        </div>
        <div className="flex px-4 pb-2">
          {['Campaign', 'Keywords', 'Ad Creative', 'Review'].map((label, i) => (
            <div key={label} className="flex-1 text-center">
              <span className={`text-xs ${i + 1 === step ? 'text-primary-500 font-medium' : 'text-gray-400'}`}>{label}</span>
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              <span>Campaign created successfully as PAUSED. Syncing data...</span>
            </div>
          )}

          {/* Step 1: Campaign Settings */}
          {step === 1 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Campaign Name *</label>
                <input
                  type="text"
                  value={form.campaign_name}
                  onChange={e => updateForm('campaign_name', e.target.value)}
                  placeholder="e.g. HVAC Repair - Austin TX"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  maxLength={255}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Daily Budget *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={form.daily_budget}
                    onChange={e => updateForm('daily_budget', e.target.value)}
                    placeholder="50.00"
                    min="1"
                    step="0.01"
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white pl-8 pr-4 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bidding Strategy *</label>
                <select
                  value={form.bidding_strategy}
                  onChange={e => updateForm('bidding_strategy', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                >
                  {BIDDING_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  {BIDDING_OPTIONS.find(o => o.value === form.bidding_strategy)?.description}
                </p>
              </div>

              {form.bidding_strategy === 'manual_cpc' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default CPC Bid *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      value={form.cpc_bid}
                      onChange={e => updateForm('cpc_bid', e.target.value)}
                      placeholder="2.50"
                      min="0.01"
                      step="0.01"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white pl-8 pr-4 py-2"
                    />
                  </div>
                </div>
              )}

              {form.bidding_strategy === 'target_cpa' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target CPA *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                      type="number"
                      value={form.target_cpa}
                      onChange={e => updateForm('target_cpa', e.target.value)}
                      placeholder="25.00"
                      min="0.01"
                      step="0.01"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white pl-8 pr-4 py-2"
                    />
                  </div>
                </div>
              )}

              {form.bidding_strategy === 'target_roas' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target ROAS *</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={form.target_roas}
                      onChange={e => updateForm('target_roas', e.target.value)}
                      placeholder="3.0"
                      min="0.01"
                      step="0.1"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">x</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">e.g. 3.0 = $3 revenue per $1 spent</p>
                </div>
              )}

              {/* Location Targeting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Target Locations
                  <span className="text-gray-400 font-normal ml-1">(optional)</span>
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={locationQuery}
                      onChange={e => setLocationQuery(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), searchLocations())}
                      placeholder="Search zip code, city, or state..."
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white pl-9 pr-4 py-2"
                    />
                  </div>
                  <button
                    onClick={searchLocations}
                    disabled={locationQuery.length < 2 || locationSearching}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {locationSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </button>
                </div>

                {/* Search Results */}
                {locationResults.length > 0 && (
                  <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg max-h-40 overflow-y-auto">
                    {locationResults.map(loc => (
                      <button
                        key={loc.id}
                        onClick={() => addLocation(loc)}
                        disabled={form.locations.some(l => l.id === loc.id)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm border-b border-gray-100 dark:border-gray-800 last:border-0 disabled:opacity-40"
                      >
                        <span className="text-gray-900 dark:text-white">{loc.canonical_name}</span>
                        <span className="text-xs text-gray-400 ml-2">{loc.target_type}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected Locations */}
                {form.locations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {form.locations.map(loc => (
                      <span key={loc.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                        <MapPin className="w-3 h-3" />
                        {loc.canonical_name}
                        <button onClick={() => removeLocation(loc.id)} className="hover:text-red-500 ml-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {form.locations.length === 0 && (
                  <p className="text-xs text-gray-400 mt-1">Leave empty to target all locations. Search by zip code (e.g. 78701), city, or state.</p>
                )}
              </div>
            </>
          )}

          {/* Step 2: Ad Group & Keywords */}
          {step === 2 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ad Group Name *</label>
                <input
                  type="text"
                  value={form.ad_group_name}
                  onChange={e => updateForm('ad_group_name', e.target.value)}
                  placeholder={`${form.campaign_name || 'Campaign'} - Ad Group`}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  maxLength={255}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Keywords * <span className="text-gray-400">({validKeywordCount} added)</span>
                  </label>
                  <button
                    onClick={addKeyword}
                    className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Add
                  </button>
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {form.keywords.map((kw, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={kw.text}
                        onChange={e => updateKeyword(i, 'text', e.target.value)}
                        placeholder="e.g. hvac repair austin"
                        className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-1.5 text-sm"
                      />
                      <select
                        value={kw.match_type}
                        onChange={e => updateKeyword(i, 'match_type', e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-2 py-1.5 text-sm w-24"
                      >
                        {MATCH_TYPES.map(mt => (
                          <option key={mt.value} value={mt.value}>{mt.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeKeyword(i)}
                        disabled={form.keywords.length <= 1}
                        className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bulk Add Keywords
                </label>
                <textarea
                  value={bulkKeywords}
                  onChange={e => setBulkKeywords(e.target.value)}
                  placeholder={"One keyword per line:\nhvac repair austin\n[exact match keyword]\n\"phrase match keyword\""}
                  rows={4}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm font-mono"
                />
                <button
                  onClick={parseBulkKeywords}
                  disabled={!bulkKeywords.trim()}
                  className="mt-1 text-sm text-primary-500 hover:text-primary-600 disabled:opacity-50"
                >
                  Parse & Add Keywords
                </button>
                <p className="text-xs text-gray-400 mt-1">
                  Use [brackets] for exact, "quotes" for phrase, plain text for broad match
                </p>
              </div>
            </>
          )}

          {/* Step 3: Ad Creative */}
          {step === 3 && (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Headlines * <span className="text-gray-400">({validHeadlineCount}/3-15, max 30 chars)</span>
                  </label>
                  {form.headlines.length < 15 && (
                    <button onClick={addHeadline} className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1">
                      <Plus className="w-4 h-4" /> Add
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {form.headlines.map((h, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={h}
                          onChange={e => updateHeadline(i, e.target.value)}
                          placeholder={`Headline ${i + 1}`}
                          maxLength={30}
                          className={`w-full border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-1.5 text-sm pr-12 ${
                            h.length > 30 ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${h.length > 30 ? 'text-red-500' : 'text-gray-400'}`}>
                          {h.length}/30
                        </span>
                      </div>
                      <button
                        onClick={() => removeHeadline(i)}
                        disabled={form.headlines.length <= 3}
                        className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Descriptions * <span className="text-gray-400">({validDescriptionCount}/2-4, max 90 chars)</span>
                  </label>
                  {form.descriptions.length < 4 && (
                    <button onClick={addDescription} className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1">
                      <Plus className="w-4 h-4" /> Add
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {form.descriptions.map((d, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <textarea
                          value={d}
                          onChange={e => updateDescription(i, e.target.value)}
                          placeholder={`Description ${i + 1}`}
                          maxLength={90}
                          rows={2}
                          className={`w-full border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-1.5 text-sm pr-12 ${
                            d.length > 90 ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        <span className={`absolute right-3 bottom-2 text-xs ${d.length > 90 ? 'text-red-500' : 'text-gray-400'}`}>
                          {d.length}/90
                        </span>
                      </div>
                      <button
                        onClick={() => removeDescription(i)}
                        disabled={form.descriptions.length <= 2}
                        className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Final URL *</label>
                <input
                  type="url"
                  value={form.final_url}
                  onChange={e => updateForm('final_url', e.target.value)}
                  placeholder="https://example.com/landing-page"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Path 1</label>
                  <input
                    type="text"
                    value={form.path1}
                    onChange={e => updateForm('path1', e.target.value)}
                    placeholder="e.g. HVAC"
                    maxLength={15}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Path 2</label>
                  <input
                    type="text"
                    value={form.path2}
                    onChange={e => updateForm('path2', e.target.value)}
                    placeholder="e.g. Repair"
                    maxLength={15}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2"
                  />
                </div>
              </div>

              {/* Ad Preview */}
              {form.final_url && validHeadlineCount >= 1 && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Ad Preview</p>
                  <p className="text-xs text-green-700 dark:text-green-400">
                    {form.final_url.replace(/^https?:\/\//, '').split('/')[0]}
                    {form.path1 && ` / ${form.path1}`}
                    {form.path2 && ` / ${form.path2}`}
                  </p>
                  <p className="text-blue-600 dark:text-blue-400 text-base font-medium">
                    {form.headlines.filter(h => h.trim()).slice(0, 3).join(' | ') || 'Your headlines here'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                    {form.descriptions.find(d => d.trim()) || 'Your description here'}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300 px-4 py-3 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                <span>Campaign will be created as <strong>PAUSED</strong>. Activate it from Google Ads when ready.</span>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Campaign</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-500">Name:</span>
                    <span className="text-gray-900 dark:text-white">{form.campaign_name}</span>
                    <span className="text-gray-500">Budget:</span>
                    <span className="text-gray-900 dark:text-white">${parseFloat(form.daily_budget || '0').toFixed(2)}/day</span>
                    <span className="text-gray-500">Bidding:</span>
                    <span className="text-gray-900 dark:text-white">
                      {BIDDING_OPTIONS.find(o => o.value === form.bidding_strategy)?.label}
                      {form.bidding_strategy === 'manual_cpc' && form.cpc_bid && ` ($${form.cpc_bid} CPC)`}
                      {form.bidding_strategy === 'target_cpa' && form.target_cpa && ` ($${form.target_cpa})`}
                      {form.bidding_strategy === 'target_roas' && form.target_roas && ` (${form.target_roas}x)`}
                    </span>
                    {form.locations.length > 0 && (
                      <>
                        <span className="text-gray-500">Locations:</span>
                        <span className="text-gray-900 dark:text-white">
                          {form.locations.map(l => l.canonical_name).join(', ')}
                        </span>
                      </>
                    )}
                    {form.locations.length === 0 && (
                      <>
                        <span className="text-gray-500">Locations:</span>
                        <span className="text-gray-400">All locations (no targeting)</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    Ad Group & Keywords
                  </h3>
                  <p className="text-sm text-gray-500 mb-2">
                    {form.ad_group_name || `${form.campaign_name} - Ad Group`}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {form.keywords.filter(k => k.text.trim()).map((kw, i) => (
                      <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        {kw.match_type === 'exact' ? `[${kw.text}]` :
                         kw.match_type === 'phrase' ? `"${kw.text}"` :
                         kw.text}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Ad Creative</h3>
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded p-3">
                    <p className="text-xs text-green-700 dark:text-green-400">
                      {form.final_url.replace(/^https?:\/\//, '').split('/')[0]}
                      {form.path1 && ` / ${form.path1}`}
                      {form.path2 && ` / ${form.path2}`}
                    </p>
                    <p className="text-blue-600 dark:text-blue-400 font-medium">
                      {form.headlines.filter(h => h.trim()).slice(0, 3).join(' | ')}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                      {form.descriptions.find(d => d.trim())}
                    </p>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    {validHeadlineCount} headlines, {validDescriptionCount} descriptions
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            disabled={creating || success}
            className="flex items-center gap-1 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !isStep1Valid()) ||
                (step === 2 && !isStep2Valid()) ||
                (step === 3 && !isStep3Valid())
              }
              className="flex items-center gap-1 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={creating || success}
              className="flex items-center gap-2 bg-primary-500 text-black px-6 py-2 rounded-lg hover:bg-primary-600 disabled:opacity-50"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : success ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Created!
                </>
              ) : (
                'Create Campaign'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignWizard;
